// Favicon loading utility with multiple fallback strategies
interface FaviconCache {
  [domain: string]: {
    url: string;
    timestamp: number;
    expires: number;
  };
}

// In-memory cache (in production, consider using localStorage or a proper cache)
const faviconCache: FaviconCache = {};

// Cache duration: 24 hours
const CACHE_DURATION = 24 * 60 * 60 * 1000;

// Brandfetch client ID for API access
const BRANDFETCH_CLIENT_ID = '1iddr807FNSCfhcKGsr';

// Multiple favicon services for fallback - Updated with Brandfetch as primary
const FAVICON_SERVICES = [
  // Primary: Brandfetch CDN (high-quality brand logos) - Larger default size
  (domain: string) => `https://cdn.brandfetch.io/${domain}/w/128/h/128?c=${BRANDFETCH_CLIENT_ID}`,
  // Brandfetch high-res for better quality
  (domain: string) => `https://cdn.brandfetch.io/${domain}/w/256/h/256?c=${BRANDFETCH_CLIENT_ID}`,
  // Brandfetch maximum quality
  (domain: string) => `https://cdn.brandfetch.io/${domain}/w/400/h/400?c=${BRANDFETCH_CLIENT_ID}`,
  // Favicon.is (reliable fallback)
  (domain: string) => `https://favicon.is/${domain}`,
  // High-res favicon.is
  (domain: string) => `https://favicon.is/${domain}?larger=true`,
  // DuckDuckGo (backup)
  (domain: string) => `https://icons.duckduckgo.com/ip3/${domain}.ico`,
  // Google's service (original fallback)
  (domain: string) => `https://www.google.com/s2/favicons?domain=${domain}&sz=32`,
  // Favicon.cc (alternative)
  (domain: string) => `https://favicon.cc/favicon/${domain}`,
  // Direct favicon.ico (common location)
  (domain: string) => `https://${domain}/favicon.ico`,
  // Apple touch icon (for modern sites)
  (domain: string) => `https://${domain}/apple-touch-icon.png`,
  // Apple touch icon with size
  (domain: string) => `https://${domain}/apple-touch-icon-180x180.png`,
];

// Clean domain name
function cleanDomain(domain: string): string {
  return domain.replace(/^www\./, '').toLowerCase();
}

// Check if a URL is accessible
async function isUrlAccessible(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, {
      method: 'HEAD',
      mode: 'no-cors', // Avoid CORS issues
    });
    return true; // If we get here, the request was made
  } catch {
    return false;
  }
}

// Test favicon URL and return the first working one
async function findWorkingFavicon(domain: string): Promise<string | null> {
  const cleanDomainName = cleanDomain(domain);
  
  // Check cache first
  const cached = faviconCache[cleanDomainName];
  if (cached && Date.now() < cached.expires) {
    return cached.url;
  }
  
  // Try each service in order
  for (const service of FAVICON_SERVICES) {
    try {
      const url = service(cleanDomainName);
      
      // For direct favicon.ico and apple-touch-icon, we need to test if they exist
      if (url.includes('/favicon.ico') || url.includes('/apple-touch-icon')) {
        const isAccessible = await isUrlAccessible(url);
        if (isAccessible) {
          // Cache the result
          faviconCache[cleanDomainName] = {
            url,
            timestamp: Date.now(),
            expires: Date.now() + CACHE_DURATION,
          };
          return url;
        }
      } else {
        // For external services, we can assume they work
        faviconCache[cleanDomainName] = {
          url,
          timestamp: Date.now(),
          expires: Date.now() + CACHE_DURATION,
        };
        return url;
      }
    } catch (error) {
      console.warn(`Favicon service failed for ${domain}:`, error);
      continue;
    }
  }
  
  return null;
}

// Main function to get favicon URL
export async function getFaviconUrl(domain: string, useEnhancedService = false, highRes = false): Promise<string> {
  try {
    // Try enhanced service first if enabled
    if (useEnhancedService) {
      try {
        const { supabase } = await import('@/integrations/supabase/client');
        const { data, error } = await supabase.functions.invoke('enhanced-favicon-service', {
          body: { domain, preferHighRes: highRes }
        });
        
        if (!error && data?.url) {
          return data.url;
        }
      } catch (error) {
        console.warn('Enhanced favicon service failed, falling back to local service:', error);
      }
    }
    
    // Use Brandfetch as primary with size based on highRes parameter
    const size = highRes ? '256' : '128';
    const faviconUrl = `https://cdn.brandfetch.io/${cleanDomain(domain)}/w/${size}/h/${size}?c=${BRANDFETCH_CLIENT_ID}`;
    return faviconUrl;
  } catch (error) {
    console.error('Error getting favicon for domain:', domain, error);
    return `https://cdn.brandfetch.io/${cleanDomain(domain)}/w/128/h/128?c=${BRANDFETCH_CLIENT_ID}`;
  }
}

// Synchronous version for immediate use (returns Brandfetch as primary fallback)
export function getFaviconUrlSync(domain: string, highRes = false): string {
  const cleanDomainName = cleanDomain(domain);
  const size = highRes ? '256' : '128';
  return `https://cdn.brandfetch.io/${cleanDomainName}/w/${size}/h/${size}?c=${BRANDFETCH_CLIENT_ID}`;
}

// Preload favicons for better performance
export async function preloadFavicons(domains: string[]): Promise<void> {
  const uniqueDomains = [...new Set(domains.map(cleanDomain))];
  
  // Preload in parallel with a limit to avoid overwhelming the browser
  const batchSize = 5;
  for (let i = 0; i < uniqueDomains.length; i += batchSize) {
    const batch = uniqueDomains.slice(i, i + batchSize);
    await Promise.allSettled(
      batch.map(domain => getFaviconUrl(domain))
    );
    
    // Small delay between batches
    if (i + batchSize < uniqueDomains.length) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
}

// Preload favicons using Brandfetch for better performance
export async function preloadFaviconsWithBrandfetch(domains: string[], highRes = false): Promise<void> {
  const uniqueDomains = [...new Set(domains.map(cleanDomain))];
  const batchSize = 10; // Increased batch size since Brandfetch is more reliable
  for (let i = 0; i < uniqueDomains.length; i += batchSize) {
    const batch = uniqueDomains.slice(i, i + batchSize);
    const preloadPromises = batch.map(domain => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve(); // Don't fail on error, just continue
        const size = highRes ? '256' : '128';
        img.src = `https://cdn.brandfetch.io/${domain}/w/${size}/h/${size}?c=${BRANDFETCH_CLIENT_ID}`;
        
        // Cache the successful URL
        faviconCache[domain] = {
          url: img.src,
          timestamp: Date.now(),
          expires: Date.now() + CACHE_DURATION,
        };
      });
    });
    
    await Promise.allSettled(preloadPromises);
    
    // Small delay between batches
    if (i + batchSize < uniqueDomains.length) {
      await new Promise(resolve => setTimeout(resolve, 50)); // Reduced delay
    }
  }
}

// Clear cache (useful for testing or when cache becomes stale)
export function clearFaviconCache(): void {
  Object.keys(faviconCache).forEach(key => {
    delete faviconCache[key];
  });
}

// Get cache statistics
export function getFaviconCacheStats(): { size: number; entries: string[] } {
  return {
    size: Object.keys(faviconCache).length,
    entries: Object.keys(faviconCache),
  };
} 